"use client";

import * as React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

function ResponsiveModal({
  open,
  onOpenChange,
  children,
}: ResponsiveModalProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        {children}
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
    </Dialog>
  );
}

interface ResponsiveModalContentProps {
  children: React.ReactNode;
  className?: string;
  /** Extra classes applied only to the desktop Dialog variant */
  dialogClassName?: string;
  /** Extra classes applied only to the mobile Drawer variant */
  drawerClassName?: string;
  /** Extra classes applied to the Dialog overlay (e.g. z-index overrides) */
  overlayClassName?: string;
  showCloseButton?: boolean;
}

function ResponsiveModalContent({
  children,
  className,
  dialogClassName,
  drawerClassName,
  overlayClassName,
  showCloseButton = false,
}: ResponsiveModalContentProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <DrawerContent className={cn(className, drawerClassName)}>
        <div className="overflow-y-auto flex-1 modern-scrollbar">
          {children}
        </div>
      </DrawerContent>
    );
  }

  return (
    <DialogContent
      showCloseButton={showCloseButton}
      className={cn(className, dialogClassName)}
      overlayClassName={overlayClassName}
    >
      {children}
    </DialogContent>
  );
}

function ResponsiveModalTitle({
  children,
  className,
  ...props
}: React.ComponentProps<"h2"> & { children: React.ReactNode }) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <DrawerTitle className={className} {...(props as any)}>
        {children}
      </DrawerTitle>
    );
  }

  return (
    <DialogTitle className={className} {...(props as any)}>
      {children}
    </DialogTitle>
  );
}

export { ResponsiveModal, ResponsiveModalContent, ResponsiveModalTitle };
